import { useEffect, useRef, useState } from 'react'
import type { FormEvent } from 'react'
import { getBrowserTimeZone } from '../utils/timezone'
import { getSessionToken } from '../utils/sessionAuth'
import { createUser } from '../api/user'
import PhoneVerificationPopup from '../components/PhoneVerificationPopup'
import '../style/views/SignupView.css'

type SignupViewProps = {
  sessionEmail: string | null
  onComplete?: () => Promise<void> | void
  onLogout?: () => void
}

type SignupPayload = {
  email: string
  cell: string
  first_name: string
  last_name: string
  pin: string
  timezone: string
}

function SignupView({ sessionEmail, onComplete, onLogout }: SignupViewProps) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [cellDigits, setCellDigits] = useState<string[]>(Array(10).fill(''))
  const [email, setEmail] = useState(sessionEmail ?? '')
  const [pin, setPin] = useState<string[]>(['', '', '', ''])
  const [phoneVerifyOpen, setPhoneVerifyOpen] = useState(false)
  const [pendingPayload, setPendingPayload] = useState<SignupPayload | null>(null)
  const [timezone, setTimezone] = useState<string>(getBrowserTimeZone())
  const [acceptedTerms, setAcceptedTerms] = useState(false)
  const [smsConsent, setSmsConsent] = useState(false)
  const [consentError, setConsentError] = useState<string | null>(null)
  const [status, setStatus] = useState<{ type: 'error' | 'success'; message: string } | null>(
    null,
  )
  const [submitting, setSubmitting] = useState(false)
  const pinRefs = useRef<Array<HTMLInputElement | null>>([])
  const phoneRefs = useRef<Array<HTMLInputElement | null>>([])

  useEffect(() => {
    if (sessionEmail && sessionEmail !== email) {
      setEmail(sessionEmail)
    }
  }, [sessionEmail, email])

  useEffect(() => {
    setTimezone(getBrowserTimeZone())
  }, [])

  const updateCellDigit = (index: number, value: string) => {
    const digit = value.slice(-1).replace(/[^0-9]/g, '')
    const next = [...cellDigits]
    next[index] = digit
    setCellDigits(next)
    if (digit && index < cellDigits.length - 1) {
      phoneRefs.current[index + 1]?.focus()
    }
  }

  const handleCellKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (cellDigits[index]) {
        const next = [...cellDigits]
        next[index] = ''
        setCellDigits(next)
      } else if (index > 0) {
        const next = [...cellDigits]
        next[index - 1] = ''
        setCellDigits(next)
        phoneRefs.current[index - 1]?.focus()
      }
    }
  }

  const updatePin = (index: number, value: string) => {
    const digit = value.slice(-1).replace(/[^0-9]/g, '')
    const next = [...pin]
    next[index] = digit
    setPin(next)

    if (digit && index < pin.length - 1) {
      pinRefs.current[index + 1]?.focus()
    }
  }

  const handlePinKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace') {
      event.preventDefault()
      if (pin[index]) {
        const next = [...pin]
        next[index] = ''
        setPin(next)
      } else if (index > 0) {
        const next = [...pin]
        next[index - 1] = ''
        setPin(next)
        pinRefs.current[index - 1]?.focus()
      }
    }
  }

  const handlePhoneVerified = async () => {
    if (!pendingPayload) {
      throw new Error('Missing signup details. Please try again.')
    }

    setSubmitting(true)
    try {
      // POST to user-service with Auth Token
      await createUser(
        {
          email,
          firstName,
          lastName,
          cell: pendingPayload.cell,
          pin: pendingPayload.pin,
          timezone,
        },
        getSessionToken() ?? undefined,
      )

      setStatus({ type: 'success', message: 'Account saved. Redirecting…' })
      if (typeof window !== 'undefined') {
        window.localStorage.setItem('myscribbl_onboarding_status', 'pending')
      }
      await onComplete?.()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO(ENG-399): replace with proper type
    } catch (err: any) {
      console.error('Signup profile completion error:', err)
      setStatus({ type: 'error', message: err.message || 'Failed to save account details.' })
    } finally {
      setSubmitting(false)
    }
  }

  const handlePhoneVerifyClose = () => {
    setPhoneVerifyOpen(false)
    setPendingPayload(null)
  }

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setStatus(null)
    setConsentError(null)
    if (!acceptedTerms || !smsConsent) {
      setConsentError('Please agree to the Privacy Policy, Terms, and SMS updates to continue.')
      return
    }
    const trimmedPin = pin.join('').trim()
    const rawCell = cellDigits.join('').trim()
    const formattedCell =
      rawCell.length >= 10
        ? `+1${rawCell.slice(0, 10)}`
        : ''
    if (!firstName.trim() || !lastName.trim() || !email.trim() || rawCell.length < 10) {
      setStatus({ type: 'error', message: 'Please fill out all fields.' })
      return
    }
    if (!/^[0-9]{4}$/.test(trimmedPin)) {
      setStatus({ type: 'error', message: 'PIN must be exactly 4 digits.' })
      return
    }

    setSubmitting(true)
    try {
      const payload: SignupPayload = {
        email,
        cell: formattedCell,
        first_name: firstName,
        last_name: lastName,
        pin: trimmedPin,
        timezone,
      }
      setPendingPayload(payload)
      setPhoneVerifyOpen(true)
    } catch (err) {
      console.error('Failed to open phone verification', err)
      setPendingPayload(null)
      setStatus({
        type: 'error',
        message: 'Unable to start phone verification. Please try again.',
      })
      setPhoneVerifyOpen(false)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="signup-layout">
      <div className="signup-hero">
        {onLogout && (
          <button type="button" className="signup-signout-button" onClick={onLogout}>
            Sign out
          </button>
        )}
        <p className="eyebrow">Welcome</p>
        <h1>Finish setting up your account</h1>
        <p className="muted">
          Add a few details below to get started with myScribbl.
        </p>
      </div>

      <div className="card signup-card">
        <form onSubmit={handleSubmit} className="signup-form">
          <div className="form-grid">
            <label>
              <span>First name</span>
              <input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </label>
            <label>
              <span>Last name</span>
              <input value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </label>
          </div>

          <div className="form-row-block">
            <label className="full-row">
              <span>Phone number</span>
              <div className="phone-row">
                {cellDigits.map((digit, pos) => (
                  <input
                    key={`phone-${pos}`}
                    className="pin-input"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => updateCellDigit(pos, e.target.value)}
                    onKeyDown={(e) => handleCellKeyDown(pos, e)}
                    ref={(el) => {
                      phoneRefs.current[pos] = el
                    }}
                  />
                ))}
              </div>
            </label>
          </div>

          <div className="form-row-block">
            <label className="full-row">
              <span>4-digit PIN</span>
              <div className="pin-row">
                {pin.map((digit, pos) => (
                  <input
                    key={`pin-${pos}`}
                    className="pin-input"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => updatePin(pos, e.target.value)}
                    onKeyDown={(e) => handlePinKeyDown(pos, e)}
                    ref={(el) => {
                      pinRefs.current[pos] = el
                    }}
                  />
                ))}
              </div>
            </label>
          </div>

          <div className="form-divider" />

          <div className="form-row-block">
            <div className="signup-consent-list">
              <div className="signup-consent">
                <input
                  id="signup-consent"
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setAcceptedTerms(checked)
                    if (checked && smsConsent) setConsentError(null)
                  }}
                />
                <label htmlFor="signup-consent">
                  I agree to the{' '}
                  <a
                    href="https://www.myscribbl.com/privacy-policy"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://www.myscribbl.com/terms-conditions"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Terms &amp; Conditions
                  </a>
                  .
                </label>
              </div>
              <div className="signup-consent">
                <input
                  id="signup-sms-consent"
                  type="checkbox"
                  checked={smsConsent}
                  onChange={(e) => {
                    const checked = e.target.checked
                    setSmsConsent(checked)
                    if (checked && acceptedTerms) setConsentError(null)
                  }}
                />
                <label htmlFor="signup-sms-consent">
                  I agree to receive SMS updates from myScribbl.
                </label>
              </div>
            </div>
            {consentError && (
              <span className="signup-status signup-consent-error error">{consentError}</span>
            )}
          </div>

          <div className="signup-actions">
            <button
              type="submit"
              className="primary-button"
              disabled={submitting || phoneVerifyOpen}
            >
              {submitting ? 'Starting…' : 'Create account'}
            </button>
            {status && <span className={`signup-status ${status.type}`}>{status.message}</span>}
          </div>
        </form>
      </div>
      <PhoneVerificationPopup
        open={phoneVerifyOpen}
        cell={pendingPayload?.cell ?? ''}
        onClose={handlePhoneVerifyClose}
        onVerified={handlePhoneVerified}
        description="Enter the 4-digit code sent to your phone to finish creating your account."
      />
    </div>
  )
}

export default SignupView
